import { useCallback, useEffect, useState } from 'react'

import { useConnect } from 'redux-bundler-hook'

import { Download as DownloadIcon } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import { Dialog, DialogContent, IconButton, Stack } from '@mui/material'

import { Loading } from '@common/components'
import { parseApiErrors } from '@common/utils'

import { GENERIC_PDF_EXPORT_ERROR } from '../reportExportBundle'
import NoiseReportContent from './NoiseReportContent'
import { closeIconStyles, exportButtonStyles, loaderStyles } from './Report.styles'

/**
 * @component
 * @param {Object} props - The props for the component.
 * @param {Function} props.onClose
 * @param {Object} props.instance
 * @param {string} props.instance.id
 * @param {string} props.instance.accountName
 * @param {string} props.instance.propertyName
 * @param {string} props.instance.property
 * @param {string} props.instance.unitName
 * @param {string} props.instance.zone
 * @param {string} props.instance.device
 * @param {string} props.instance.createdOn
 * @param {string} [props.instance.endTime]
 * @param {Object} [props.instance.noiseFeedback]
 * @param {string} [props.instance.noiseFeedback.id]
 * @param {string} [props.instance.noiseFeedback.comments]
 */
export default function NoiseReport({ onClose, instance }) {
  const [reportData, setReportData] = useState(null)

  const [feedback, setFeedback] = useState(instance.noiseFeedback?.comments || '')
  const [initialFeedback, setInitialFeedback] = useState({
    id: instance.noiseFeedback?.id,
    comments: instance.noiseFeedback?.comments || '',
  })

  const {
    noiseReportExportIsLoading,
    noiseReportDataIsLoading,
    doFetchNoiseReportData,
    doMarkEventListAsOutdated,
    doNoiseEventFeedbackSave,
    doExportNoiseReport,
    doShowSnackbar,
  } = useConnect(
    'selectNoiseReportExportIsLoading',
    'selectNoiseReportDataIsLoading',
    'doFetchNoiseReportData',
    'doMarkEventListAsOutdated',
    'doNoiseEventFeedbackSave',
    'doExportNoiseReport',
    'doShowSnackbar',
  )

  const fetchReportData = useCallback(async () => {
    try {
      const data = await doFetchNoiseReportData(instance.id)
      setReportData(data)
    } catch (err) {
      const parsedError = parseApiErrors(err?.response)
      doShowSnackbar(parsedError || 'Unable to load chart', 'error')
    }
  }, [instance])

  useEffect(() => {
    fetchReportData()
  }, [instance])

  const downloadReportPDF = useCallback(async () => {
    try {
      await doExportNoiseReport(instance.id)
    } catch (error) {
      doShowSnackbar(GENERIC_PDF_EXPORT_ERROR, 'error')
    }
  }, [instance])

  const handleSaveFeedback = async () => {
    const payload = {
      comments: feedback,
      event: instance.id,
      property: instance.property,
    }

    if (initialFeedback.id) {
      payload.id = initialFeedback.id
    }

    const result = await doNoiseEventFeedbackSave(payload)
    if (result.error) {
      doShowSnackbar('Unable to save Feedback', 'error')
      return
    }
    setInitialFeedback({ id: result.id, comments: feedback })
    doShowSnackbar('Feedback saved', 'success')
    doMarkEventListAsOutdated()
  }

  return (
    <Dialog open data-testid="noise_report_dialog" maxWidth="lg" onClose={onClose}>
      <DialogContent
        sx={{
          position: 'relative',
          backgroundColor: '#1f2a44',
        }}
      >
        <IconButton
          onClick={onClose}
          sx={closeIconStyles}
          disabled={noiseReportExportIsLoading}
        >
          <CloseIcon />
        </IconButton>
        <IconButton
          sx={exportButtonStyles}
          onClick={downloadReportPDF}
          disabled={noiseReportExportIsLoading}
        >
          <DownloadIcon />
        </IconButton>
        {noiseReportExportIsLoading && (
          <Loading containerStyles={loaderStyles} size={20} color="white" />
        )}
        <Stack position="relative">
          <NoiseReportContent
            loading={noiseReportDataIsLoading}
            data={reportData}
            initialFeedback={initialFeedback}
            feedback={feedback}
            onSaveFeedback={handleSaveFeedback}
            onChangeFeedback={setFeedback}
          />
        </Stack>
      </DialogContent>
    </Dialog>
  )
}
